import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import {
  PersonaGroup,
  PersonaWithConfig,
  Persona,
  PersonaConfig,
  PersonaOptionsResponse,
  SystemPersonaWithConfig,
  PersonaAgentConfig as AgentConfig,
  ParamSchema,
  type DeletePersonaResult,
} from '@localagent/sdk';

/**
 * Every cache a persona write invalidates.
 *
 * The session keys are the non-obvious half and the reason this is one
 * function: a persisted session does not read the persona, it reads
 * `session.effective_agent_config`, which the SERVER derives from the persona
 * (api/sessions.go -> api/reasoning.go). Invalidating only the `agent-*` keys
 * left an open chat rendering a stale model chip and stale process options —
 * including a stale "resolved to an empty provider/model" banner — until
 * something unrelated refetched the session.
 *
 * Session DETAIL keys only (`['sessions', workspaceId, sessionId]`): the
 * message list hangs off the same prefix and must not be refetched here.
 */
function invalidatePersonaCaches(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['agent-definitions'] });
  void queryClient.invalidateQueries({ queryKey: ['agents'] });
  void queryClient.invalidateQueries({ queryKey: ['agent-groups'] });
  void queryClient.invalidateQueries({ queryKey: ['agent-options'] });
  void queryClient.invalidateQueries({ queryKey: ['agent-definition-by-id'] });
  void queryClient.invalidateQueries({ queryKey: ['agent-definition-config-by-id'] });
  queryClient.removeQueries({ queryKey: ['agent-definition'] });
  queryClient.removeQueries({ queryKey: ['agent-definition-config'] });
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'sessions' && query.queryKey.length === 3,
  });
}

export function useAgentGroups() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<PersonaGroup[], Error>({
    queryKey: ['agent-groups'],
    queryFn: () => getSDK().persona.listGroups(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useAgentOptions() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<PersonaOptionsResponse, Error>({
    queryKey: ['agent-options'],
    queryFn: () => getSDK().persona.getOptions(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgentOptionsCompat() {
  return useAgentOptions();
}

export function useCreateAgentGroup() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<PersonaGroup, Error, { name: string; description?: string }>({
    mutationFn: ({ name, description }) => {
      return getSDK().persona.createGroup({ name, description });
    },
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['agent-groups'] });
        void queryClient.invalidateQueries({ queryKey: ['agent-options'] });
      }
    },
  });
}

export function useCreateAgentGroupCompat() {
  return useCreateAgentGroup();
}

export function useDeleteAgentGroup() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (groupId) => {
      return getSDK().persona.deleteGroup({ group_id: groupId });
    },
    onSuccess: () => {
      if (isAuthenticated) {
        void queryClient.invalidateQueries({ queryKey: ['agent-groups'] });
        void queryClient.invalidateQueries({ queryKey: ['agent-definitions'] });
        void queryClient.invalidateQueries({ queryKey: ['agent-options'] });
      }
    },
  });
}

export function useAgentDefinitions(groupName?: string) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<PersonaWithConfig[], Error>({
    queryKey: ['agent-definitions', groupName],
    queryFn: async () => {
      const sdk = getSDK();
      const personas = await sdk.persona.listPersonas({
        group_name: groupName,
        include_configs: true,
      });

      return personas.map(persona => {
        const config = persona.latest_config;
        if (!config) {
          throw new Error(`persona ${persona.name} (${persona.id}) has no latest config`);
        }
        const parsed = JSON.parse(config.agent_config_json) as AgentConfig;
        const agent_config: AgentConfig = {
          ...parsed,
          completion_params: parsed.completion_params ?? {},
        };
        return {
          ...persona,
          config,
          agent_config,
        };
      });
    },
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useAgentDefinitionsCompat(groupName?: string) {
  return useAgentDefinitions(groupName);
}

export function useSystemAgents() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<SystemPersonaWithConfig[], Error>({
    queryKey: ['system-agents'],
    queryFn: () => getSDK().persona.listActiveSystemPersonas(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
    staleTime: 10 * 60 * 1000,
  });
}

export function useAgentDefinitionById(personaId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<Persona, Error>({
    queryKey: ['agent-definition-by-id', personaId],
    queryFn: () => getSDK().persona.getPersona({ persona_id: personaId! }),
    enabled: Boolean(isAuthenticated && sdkConfigured && personaId),
  });
}

export function useAgentDefinitionByIdCompat(personaId: string | undefined) {
  return useAgentDefinitionById(personaId);
}

export function useAgentDefinitionConfigById(personaId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<AgentConfig, Error>({
    queryKey: ['agent-definition-config-by-id', personaId],
    queryFn: async () => {
      const configRecord = await getSDK().persona.getLatestConfig({ persona_id: personaId! });
      const parsed = JSON.parse(configRecord.agent_config_json) as AgentConfig;
      return {
        ...parsed,
        completion_params: parsed.completion_params ?? {},
      };
    },
    enabled: Boolean(isAuthenticated && sdkConfigured && personaId),
  });
}

export function useAgentDefinitionConfigByIdCompat(personaId: string | undefined) {
  return useAgentDefinitionConfigById(personaId);
}

interface CreateAgentDefinitionRequest {
  name: string;
  agentConfig: AgentConfig;
  description?: string;
  groupName?: string;
  /** From getParamSchema. Unconditional — an empty one is a 422 (persona ADR-020). */
  fingerprint: string;
}

export function useCreateAgentDefinition() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<PersonaConfig, Error, CreateAgentDefinitionRequest>({
    mutationFn: async (request) => {
      const agentConfig: AgentConfig = {
        ...request.agentConfig,
        completion_params: {
          ...request.agentConfig.completion_params,
        },
      };

      return await getSDK().persona.createPersonaWithConfig({
        name: request.name,
        agent_config: agentConfig,
        description: request.description,
        group_name: request.groupName,
        fingerprint: request.fingerprint,
      });
    },
    onSuccess: () => {
      if (isAuthenticated) invalidatePersonaCaches(queryClient);
    },
  });
}

export function useCreateAgentDefinitionCompat() {
  return useCreateAgentDefinition();
}

export function useUpdateAgentDefinition() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<Persona, Error, {
    id: string;
    updates: { name?: string; description?: string; group_name?: string };
    agentConfig?: AgentConfig;
    fingerprint?: string;
  }>({
    mutationFn: async ({ id, updates, agentConfig, fingerprint }) => {
      const sdk = getSDK();
      const persona = await sdk.persona.updatePersona({ persona_id: id, ...updates });
      if (agentConfig) {
        await sdk.persona.createNewConfig({
          persona_id: id,
          agent_config: agentConfig,
          fingerprint: fingerprint ?? (await fingerprintFor(agentConfig)),
        });
      }
      return persona;
    },
    onSuccess: () => {
      if (isAuthenticated) invalidatePersonaCaches(queryClient);
    },
  });
}

export function useUpdateAgentDefinitionCompat() {
  return useUpdateAgentDefinition();
}

export function useDeleteAgentDefinition() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<DeletePersonaResult, Error, { personaId: string; deleteAgents: boolean }>({
    mutationFn: ({ personaId, deleteAgents }) => {
      return getSDK().persona.deletePersona({ persona_id: personaId, delete_agents: deleteAgents });
    },
    onSuccess: () => {
      if (isAuthenticated) invalidatePersonaCaches(queryClient);
    },
  });
}

interface DuplicateAgentDefinitionRequest {
  name: string;
  agentConfig: AgentConfig;
  description?: string;
  groupName?: string;
}

/**
 * Duplicate has no editor schema to echo, so it resolves the fingerprint for
 * the config it is copying. Sending none is a 422 (persona ADR-020).
 */
async function fingerprintFor(config: AgentConfig): Promise<string> {
  if (!config.provider_type || !config.model_name) {
    throw new Error('agent config names no provider_type/model_name');
  }
  const schema = await getSDK().persona.getParamSchema(config.provider_type, config.model_name);
  if (!schema.fingerprint) {
    throw new Error(`getParamSchema(${config.provider_type}/${config.model_name}) returned no fingerprint`);
  }
  return schema.fingerprint;
}

export function useDuplicateAgentDefinition() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<PersonaConfig, Error, DuplicateAgentDefinitionRequest>({
    mutationFn: async (request) => {
      return await getSDK().persona.createPersonaWithConfig({
        name: request.name,
        agent_config: request.agentConfig,
        description: request.description,
        group_name: request.groupName,
        fingerprint: await fingerprintFor(request.agentConfig),
      });
    },
    onSuccess: () => {
      if (isAuthenticated) invalidatePersonaCaches(queryClient);
    },
  });
}

export function useCopySystemAgent() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<{ persona_id: string; config_id: string; name: string }, Error, { systemPersonaId: string; newName?: string }>({
    mutationFn: ({ systemPersonaId, newName }) => {
      return getSDK().persona.copySystemPersona({ system_persona_id: systemPersonaId, new_name: newName });
    },
    onSuccess: () => {
      if (isAuthenticated) invalidatePersonaCaches(queryClient);
    },
  });
}

/**
 * The resolved parameter schema for one provider/model
 * (`GET /v1/persona/param-schema`).
 *
 * This is the editor's SINGLE source for which controls exist, what bounds they
 * carry, what the effective defaults are, and where the model sits in its
 * lifecycle. It matters that it is the same server-side resolver the three write
 * guards use (persona ADR-002): the previous source — `getOptions()`'s
 * `model_capabilities` — is a second projection, and any editor rendering from a
 * second projection can render a control the backend rejects. `getOptions()` is
 * still the right source for the model PICKER (it lists every model), just not
 * for the param grid.
 *
 * Disabled until a model is actually selected: a provider change deliberately
 * blanks `model_name` to force an explicit choice, and there is no schema to ask
 * for until one is made.
 */
export function useParamSchema(providerType: string | undefined, modelName: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ParamSchema, Error>({
    queryKey: ['param-schema', providerType, modelName],
    queryFn: () => getSDK().persona.getParamSchema(providerType!, modelName!),
    enabled: Boolean(isAuthenticated && sdkConfigured && providerType && modelName),
  });
}
